import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ShieldAlert, Trash2, RotateCcw, Search, Loader2, AlertTriangle, CheckCircle2, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DataHygieneSection({ token }) {
  const [tab, setTab] = useState('scan'); // 'scan' | 'excluded'
  const [noisyContacts, setNoisyContacts] = useState([]);
  const [excludedContacts, setExcludedContacts] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [loadingExcluded, setLoadingExcluded] = useState(false);
  const [excludingId, setExcludingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [hasScanned, setHasScanned] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const scanNoisy = useCallback(async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/data-hygiene/noisy-contacts?min_memories=5`, { headers });
      setNoisyContacts(res.data.contacts || []);
      setHasScanned(true);
      if ((res.data.contacts || []).length === 0) {
        toast.success('Aucun contact système détecté');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du scan');
    } finally {
      setScanning(false);
    }
  }, [token]);

  const loadExcluded = useCallback(async () => {
    setLoadingExcluded(true);
    try {
      const res = await axios.get(`${API}/data-hygiene/excluded`, { headers });
      setExcludedContacts(res.data.contacts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoadingExcluded(false);
    }
  }, [token]);

  useEffect(() => {
    if (tab === 'excluded') {
      loadExcluded();
    }
  }, [tab, loadExcluded]);

  const excludeContact = async (contactId, email) => {
    setExcludingId(contactId);
    try {
      const res = await axios.post(
        `${API}/data-hygiene/exclude/${contactId}?delete_memories=true`,
        {},
        { headers }
      );
      toast.success(`${email || 'Contact'} exclu — ${res.data.memories_deleted} mémoires supprimées`);
      setNoisyContacts(prev => prev.filter(c => c.contact_id !== contactId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'exclusion");
    } finally {
      setExcludingId(null);
    }
  };

  const restoreContact = async (contactId) => {
    setRestoringId(contactId);
    try {
      await axios.post(`${API}/data-hygiene/restore/${contactId}`, {}, { headers });
      toast.success('Contact restauré');
      setExcludedContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setRestoringId(null);
    }
  };

  const totalMemories = noisyContacts.reduce((sum, c) => sum + c.memory_count, 0);

  return (
    <Card data-testid="data-hygiene-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-500" />
          <CardTitle className="font-heading">Hygiène des données</CardTitle>
        </div>
        <CardDescription>
          Détectez et excluez les contacts système (robots, notifications) qui polluent la mémoire de vos clones
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tab switcher */}
        <div className="flex gap-2 border-b pb-2" data-testid="hygiene-tab-switcher">
          <button
            onClick={() => setTab('scan')}
            className={`px-3 py-1.5 text-sm rounded-t transition-colors ${
              tab === 'scan'
                ? 'bg-primary text-primary-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="hygiene-tab-scan"
          >
            <Search className="w-3.5 h-3.5 inline mr-1.5" />
            Scanner
          </button>
          <button
            onClick={() => setTab('excluded')}
            className={`px-3 py-1.5 text-sm rounded-t transition-colors ${
              tab === 'excluded'
                ? 'bg-primary text-primary-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="hygiene-tab-excluded"
          >
            <Trash2 className="w-3.5 h-3.5 inline mr-1.5" />
            Exclus ({excludedContacts.length})
          </button>
        </div>

        {/* SCAN TAB */}
        {tab === 'scan' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Recherche les contacts robots (notifications Ringover, noreply, etc.) avec un grand nombre de mémoires inutiles.
              </p>
              <Button
                onClick={scanNoisy}
                disabled={scanning}
                variant="outline"
                size="sm"
                data-testid="scan-noisy-btn"
              >
                {scanning ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Scan en cours...</>
                ) : (
                  <><Search className="w-4 h-4 mr-1.5" /> Lancer le scan</>
                )}
              </Button>
            </div>

            {hasScanned && noisyContacts.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center gap-2" data-testid="noisy-summary">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm">
                  <strong>{noisyContacts.length}</strong> contact{noisyContacts.length > 1 ? 's' : ''} suspect{noisyContacts.length > 1 ? 's' : ''} détecté{noisyContacts.length > 1 ? 's' : ''} — totalisant <strong>{totalMemories.toLocaleString()}</strong> mémoires
                </span>
              </div>
            )}

            {hasScanned && noisyContacts.length === 0 && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 flex items-center gap-2" data-testid="noisy-clean">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-sm">Aucun contact système détecté. Vos données sont propres.</span>
              </div>
            )}

            {noisyContacts.length > 0 && (
              <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="noisy-contacts-list">
                {noisyContacts.map((c) => (
                  <div
                    key={c.contact_id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
                    data-testid={`noisy-contact-${c.contact_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">
                          {c.first_name} {c.last_name}
                        </span>
                        {c.is_system && (
                          <Badge variant="destructive" className="text-xs" data-testid="system-badge">Robot</Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {c.memory_count.toLocaleString()} mémoires
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {c.email && (
                          <span className="flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3" />{c.email}
                          </span>
                        )}
                        {c.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />{c.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => excludeContact(c.contact_id, c.email)}
                      disabled={excludingId === c.contact_id}
                      data-testid={`exclude-btn-${c.contact_id}`}
                    >
                      {excludingId === c.contact_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-1" /> Exclure</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EXCLUDED TAB */}
        {tab === 'excluded' && (
          <div className="space-y-3">
            {loadingExcluded ? (
              <div className="flex items-center justify-center p-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : excludedContacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-excluded">
                Aucun contact exclu pour le moment.
              </p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="excluded-contacts-list">
                {excludedContacts.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-card"
                    data-testid={`excluded-contact-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">
                        {c.first_name} {c.last_name}
                      </span>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {c.email && (
                          <span className="flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3" />{c.email}
                          </span>
                        )}
                        {c.excluded_at && (
                          <span>
                            Exclu le {new Date(c.excluded_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreContact(c.id)}
                      disabled={restoringId === c.id}
                      data-testid={`restore-btn-${c.id}`}
                    >
                      {restoringId === c.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><RotateCcw className="w-4 h-4 mr-1" /> Restaurer</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
